import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import UploadsPanel from './UploadsPanel';

const VIEW_MODES = ['grid', 'list'];

const normalizeFolder = (value) => (value || '').replace(/^\/+|\/+$/g, '');

const getFolderEmoji = (name) => {
  const lower = String(name || '').toLowerCase();
  if (lower.includes('upload')) return '⬆️';
  if (lower.includes('photo')) return '📷';
  if (lower.includes('logo')) return '🧩';
  if (lower.includes('ai')) return '✨';
  if (lower.includes('announce')) return '📣';
  return '📁';
};

const THUMB_CACHE = new Map();

const clearThumbCache = () => {
  THUMB_CACHE.forEach((objectUrl) => {
    try {
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      // ignore
    }
  });
  THUMB_CACHE.clear();
};

function AssetThumb({ asset }) {
  const [src, setSrc] = useState('');
  const [loading, setLoading] = useState(false);
  const requestUrl = asset.preview_api_url || asset.thumbnail_api_url || asset.thumbnail_url || '';

  useEffect(() => {
    let active = true;
    if (!requestUrl) {
      setSrc('');
      return () => {};
    }
    if (THUMB_CACHE.has(requestUrl)) {
      setSrc(THUMB_CACHE.get(requestUrl));
      return () => {};
    }
    const token = localStorage.getItem('token');
    const controller = new AbortController();
    setLoading(true);
    fetch(requestUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      cache: 'no-store',
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Thumb fetch failed (${res.status})`);
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        THUMB_CACHE.set(requestUrl, objectUrl);
        if (active) setSrc(objectUrl);
      })
      .catch(() => {
        if (active) setSrc('');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [requestUrl]);

  if (!requestUrl) return <div className="text-2xl">🖼️</div>;
  if (!src && loading) return <div className="text-sm text-gray-500">Loading…</div>;
  return src ? <img src={src} alt={asset.title || 'asset'} className="h-full w-full object-cover" /> : <div className="text-2xl">🖼️</div>;
}

export default function AssetManager({ siteId, onOpenPreview, onRefreshAssets }) {
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState('');
  const [assets, setAssets] = useState([]);
  const [view, setView] = useState('grid');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [moveTarget, setMoveTarget] = useState('');
  const [usageWarning, setUsageWarning] = useState(null);
  const lastSelectedIndexRef = useRef(-1);
  const uploadsPanelRef = useRef(null);

  const folderList = useMemo(() => {
    const normalized = folders.map(normalizeFolder).filter(Boolean);
    const unique = Array.from(new Set(normalized));
    return unique.sort((a, b) => a.localeCompare(b));
  }, [folders]);

  const loadFolders = useCallback(async () => {
    if (!siteId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/assets?siteId=${encodeURIComponent(siteId)}&all=1&includeFolders=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load folders');
      setFolders(data.folders || []);
    } catch (err) {
      console.warn('Failed to load folder list', err);
    }
  }, [siteId]);

  const loadAssets = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const folderParam = normalizeFolder(currentFolder);
      const url = `/api/assets?siteId=${encodeURIComponent(siteId)}&folder=${encodeURIComponent(folderParam)}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load assets');
      setAssets(data.assets || []);
    } catch (err) {
      setError(err.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [currentFolder, siteId]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const filteredAssets = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((asset) => String(asset.title || '').toLowerCase().includes(q));
  }, [assets, search]);

  const hasSelectedImages = useMemo(
    () => assets.some((asset) => selectedIds.has(asset.id) && asset.content_type === 's3_image'),
    [assets, selectedIds]
  );

  const toggleSelect = (assetId, index, { multi = false } = {}) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (!multi) next.clear();
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
    if (typeof index === 'number') {
      lastSelectedIndexRef.current = index;
    }
  };

  const handleRowClick = (asset, index, event) => {
    if (event.shiftKey && lastSelectedIndexRef.current >= 0) {
      const start = Math.min(lastSelectedIndexRef.current, index);
      const end = Math.max(lastSelectedIndexRef.current, index);
      const slice = filteredAssets.slice(start, end + 1);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        slice.forEach((item) => next.add(item.id));
        return next;
      });
    } else {
      toggleSelect(asset.id, index, { multi: event.metaKey || event.ctrlKey });
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    lastSelectedIndexRef.current = -1;
  };

  const handleSelectAll = () => {
    if (!filteredAssets.length) return;
    setSelectedIds(new Set(filteredAssets.map((asset) => asset.id)));
  };

  const handleMove = async () => {
    if (!selectedIds.size) return;
    const folder = normalizeFolder(moveTarget);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/assets/move', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: Array.from(selectedIds), folder, siteId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to move assets');
      clearSelection();
      setMoveTarget('');
      await loadAssets();
      await loadFolders();
      onRefreshAssets?.();
    } catch (err) {
      alert(err.message || 'Failed to move assets');
    }
  };

  const handleRenameAsset = async () => {
    if (selectedIds.size !== 1) return;
    const targetId = Array.from(selectedIds)[0];
    const asset = assets.find((item) => item.id === targetId);
    const nextTitle = window.prompt('Rename asset', asset?.title || '');
    if (!nextTitle) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/assets/rename', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ id: targetId, title: nextTitle }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to rename asset');
      await loadAssets();
      onRefreshAssets?.();
    } catch (err) {
      alert(err.message || 'Failed to rename asset');
    }
  };

  const handleRenameFolder = async () => {
    const nextName = window.prompt('Rename folder', currentFolder || '');
    if (!nextName) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/assets/rename', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ folder: currentFolder, newFolder: nextName, siteId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to rename folder');
      setCurrentFolder(normalizeFolder(nextName));
      await loadFolders();
      await loadAssets();
      onRefreshAssets?.();
    } catch (err) {
      alert(err.message || 'Failed to rename folder');
    }
  };

  const handleNewFolder = async () => {
    const name = window.prompt('New folder name');
    if (!name) return;
    const normalized = normalizeFolder(name);
    if (!normalized) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/assets/folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ siteId, folder: normalized }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to create folder');
      await loadFolders();
      setCurrentFolder(normalized);
    } catch (err) {
      alert(err.message || 'Failed to create folder');
    }
  };

  const handleDelete = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} asset${selectedIds.size > 1 ? 's' : ''}?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/assets/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        setUsageWarning(data?.usage || []);
        return;
      }
      if (!res.ok) throw new Error(data?.error || 'Failed to delete assets');
      clearSelection();
      await loadAssets();
      await loadFolders();
      onRefreshAssets?.();
    } catch (err) {
      alert(err.message || 'Failed to delete assets');
    }
  };

  const handleRotate = async () => {
    if (!selectedIds.size) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/assets/rotate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to rotate assets');
      clearThumbCache();
      await loadAssets();
      onRefreshAssets?.();
    } catch (err) {
      alert(err.message || 'Failed to rotate assets');
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[240px,1fr]">
      <aside className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
        <div className="text-xs uppercase tracking-widest text-gray-400 mb-3">Folders</div>
        <button
          className={`flex w-full items-center gap-2 rounded px-3 py-2 text-sm ${
            currentFolder === '' ? 'bg-indigo-600 text-white' : 'text-gray-200 hover:bg-gray-800'
          }`}
          onClick={() => setCurrentFolder('')}
        >
          🏠 Root
        </button>
        <div className="mt-3 space-y-1">
          {folderList.map((folder) => (
            <button
              key={folder}
              className={`flex w-full items-center gap-2 rounded px-3 py-2 text-sm ${
                normalizeFolder(currentFolder) === folder ? 'bg-indigo-600 text-white' : 'text-gray-200 hover:bg-gray-800'
              }`}
              onClick={() => setCurrentFolder(folder)}
            >
              <span>{getFolderEmoji(folder)}</span>
              <span className="truncate">{folder}</span>
            </button>
          ))}
          {!folderList.length && <div className="text-xs text-gray-500">No folders yet.</div>}
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <button className="rounded bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700" onClick={handleNewFolder}>
            ➕ New Folder
          </button>
          {currentFolder && (
            <button className="rounded bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700" onClick={handleRenameFolder}>
              ✏️ Rename Folder
            </button>
          )}
        </div>
      </aside>

      <section className="rounded-lg border border-gray-800 bg-gray-900/40 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-lg font-semibold text-white flex items-center gap-2">
            📂 Asset Manager
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search assets..."
              className="rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500"
            />
            <div className="flex items-center rounded border border-gray-700 overflow-hidden">
              {VIEW_MODES.map((mode) => (
                <button
                  key={mode}
                  className={`px-3 py-2 text-xs uppercase tracking-widest ${
                    view === mode ? 'bg-indigo-600 text-white' : 'bg-gray-950 text-gray-400'
                  }`}
                  onClick={() => setView(mode)}
                >
                  {mode === 'grid' ? '◼︎' : '≡'}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <button className="rounded bg-gray-800 px-3 py-2 text-xs text-gray-200 hover:bg-gray-700" onClick={handleSelectAll}>
              Select All
            </button>
            <button className="rounded bg-gray-800 px-3 py-2 text-xs text-gray-200 hover:bg-gray-700" onClick={clearSelection}>
              Clear
            </button>
            <select
              value={moveTarget}
              onChange={(e) => setMoveTarget(e.target.value)}
              className="rounded border border-gray-700 bg-gray-950 px-2 py-2 text-xs text-gray-200"
            >
              <option value="">Move to...</option>
              <option value="">Root</option>
              {folderList.map((folder) => (
                <option key={`move-${folder}`} value={folder}>
                  {folder}
                </option>
              ))}
            </select>
            <button
              className="rounded bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
              onClick={handleMove}
              disabled={!selectedIds.size}
            >
              Move
            </button>
            <button
              className="rounded bg-gray-800 px-3 py-2 text-xs text-gray-200 hover:bg-gray-700 disabled:opacity-50"
              onClick={handleRenameAsset}
              disabled={selectedIds.size !== 1}
            >
              Rename
            </button>
            {hasSelectedImages && (
              <button
                className="rounded bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-500"
                onClick={handleRotate}
              >
                Rotate 90
              </button>
            )}
            <button
              className="rounded bg-rose-600 px-3 py-2 text-xs font-semibold text-white hover:bg-rose-500 disabled:opacity-50"
              onClick={handleDelete}
              disabled={!selectedIds.size}
            >
              Delete
            </button>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              className="rounded bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
              onClick={() => uploadsPanelRef.current?.openFileDialog?.()}
              disabled={!siteId}
            >
              Upload
            </button>
            <button
              className="rounded bg-gray-800 px-3 py-2 text-xs text-gray-200 hover:bg-gray-700"
              onClick={() => uploadsPanelRef.current?.clearCompleted?.()}
            >
              Clear Completed
            </button>
          </div>
        </div>

        {usageWarning && (
          <div className="mt-4 rounded border border-rose-700 bg-rose-950/30 p-3 text-sm text-rose-200">
            <div className="font-semibold mb-2">Cannot delete: asset is in use</div>
            <ul className="list-disc pl-5 space-y-1">
              {usageWarning.map((u) => (
                <li key={`${u.type}-${u.id}`}>{u.label}</li>
              ))}
            </ul>
          </div>
        )}

        {error && <div className="mt-4 text-sm text-rose-300">{error}</div>}
        {loading && <div className="mt-4 text-sm text-gray-400">Loading assets…</div>}

        {!loading && view === 'grid' && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredAssets.map((asset, index) => {
              const isSelected = selectedIds.has(asset.id);
              return (
                <div
                  key={asset.id}
                  className={`rounded-lg border ${
                    isSelected ? 'border-indigo-500 ring-2 ring-indigo-500' : 'border-gray-800'
                  } bg-gray-950/70 p-3`}
                >
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={(e) => handleRowClick(asset, index, e)}
                  >
                    <div className="h-32 w-full overflow-hidden rounded bg-gray-900 flex items-center justify-center">
                      <AssetThumb asset={asset} />
                    </div>
                    <div className="mt-2 text-sm font-semibold text-gray-100 truncate">{asset.title || `Asset ${asset.id}`}</div>
                    <div className="text-xs text-gray-500">{asset.type || asset.content_type}</div>
                  </button>
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelect(asset.id, index, { multi: true });
                        }}
                      />
                      Select
                    </label>
                    <button
                      className="text-indigo-300 hover:text-indigo-200"
                      onClick={() => onOpenPreview?.(asset)}
                    >
                      Preview
                    </button>
                  </div>
                </div>
              );
            })}
            {!filteredAssets.length && !loading && (
              <div className="text-sm text-gray-400">No assets in this folder.</div>
            )}
          </div>
        )}

        {!loading && view === 'list' && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm text-gray-200">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-gray-500">
                  <th className="py-2 px-2">Sel</th>
                  <th className="py-2 px-2">Asset</th>
                  <th className="py-2 px-2">Type</th>
                  <th className="py-2 px-2">Folder</th>
                  <th className="py-2 px-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssets.map((asset, index) => {
                  const isSelected = selectedIds.has(asset.id);
                  return (
                    <tr key={asset.id} className="border-t border-gray-800">
                      <td className="py-2 px-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleSelect(asset.id, index, { multi: true });
                          }}
                        />
                      </td>
                      <td className="py-2 px-2">
                        <button className="text-indigo-300 hover:text-indigo-200" onClick={(e) => handleRowClick(asset, index, e)}>
                          {asset.title || `Asset ${asset.id}`}
                        </button>
                      </td>
                      <td className="py-2 px-2 text-gray-400">{asset.type || asset.content_type}</td>
                      <td className="py-2 px-2 text-gray-400">{asset.folder || 'Root'}</td>
                      <td className="py-2 px-2 text-right">
                        <button className="text-indigo-300 hover:text-indigo-200" onClick={() => onOpenPreview?.(asset)}>
                          Preview
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!filteredAssets.length && (
                  <tr>
                    <td colSpan={5} className="py-4 text-gray-400">
                      No assets in this folder.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 rounded-lg border border-gray-800 bg-gray-950/60 p-4">
          <div className="mb-2 text-sm font-semibold text-gray-200">⬆️ Uploads</div>
          <UploadsPanel
            ref={uploadsPanelRef}
            siteId={siteId}
            showUploadControls={false}
            onUploaded={async () => {
              await loadAssets();
              await loadFolders();
              onRefreshAssets?.();
            }}
          />
        </div>
      </section>
    </div>
  );
}
